/**
 * Тема интерфейса.
 *
 * Выбор человека — «как в системе», светлая или тёмная — хранится
 * в localStorage. На странице всегда стоит итоговая тема data-theme:
 * CSS знает один тёмный селектор, а «как в системе» следит
 * за prefers-color-scheme и меняет data-theme на лету.
 */

import { useEffect, useState } from 'react'

export type ThemeChoice = 'auto' | 'light' | 'dark'

const KEY = 'svodka-theme'
const media = () => window.matchMedia('(prefers-color-scheme: dark)')

function readChoice(): ThemeChoice {
  try {
    const value = localStorage.getItem(KEY)
    return value === 'light' || value === 'dark' ? value : 'auto'
  } catch {
    return 'auto'
  }
}

function apply(choice: ThemeChoice) {
  const dark = choice === 'dark' || (choice === 'auto' && media().matches)
  document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light')
}

let current: ThemeChoice = readChoice()
const listeners = new Set<(choice: ThemeChoice) => void>()

/** Вызывается один раз при старте: тема и подписка на системную. */
export function initTheme(): void {
  apply(current)
  media().addEventListener('change', () => {
    if (current === 'auto') apply('auto')
  })
}

export function setTheme(choice: ThemeChoice): void {
  current = choice
  try {
    if (choice === 'auto') localStorage.removeItem(KEY)
    else localStorage.setItem(KEY, choice)
  } catch {
    // без хранилища выбор продержится до перезагрузки
  }
  apply(choice)
  for (const listener of listeners) listener(choice)
}

export function useTheme(): [ThemeChoice, (choice: ThemeChoice) => void] {
  const [choice, setChoice] = useState(current)
  useEffect(() => {
    listeners.add(setChoice)
    return () => {
      listeners.delete(setChoice)
    }
  }, [])
  return [choice, setTheme]
}

export const THEME_LABELS: Record<ThemeChoice, string> = {
  auto: 'Как в системе',
  light: 'Светлая',
  dark: 'Тёмная',
}
